import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import type { User, UserRole } from "@/types/user.types";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  _hasHydrated: boolean;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  login: (user: User) => void;
  logout: () => void;
  setHasHydrated: (value: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set) => ({
        // State
        user: null,
        isAuthenticated: false,
        _hasHydrated: false,

        // Actions
        setUser: (user) => set({ user, isAuthenticated: !!user }, false, "setUser"),
        login: (user) => set({ user, isAuthenticated: true }, false, "login"),
        logout: () => set({ user: null, isAuthenticated: false }, false, "logout"),
        setHasHydrated: (value) => set({ _hasHydrated: value }, false, "setHasHydrated"),
      }),
      {
        name: "tr-auth",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => (state) => {
          state?.setHasHydrated(true);
        },
      }
    ),
    { name: "auth-store", enabled: import.meta.env.DEV }
  )
);

// Selectors (prevent unnecessary re-renders)
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useUserRole = () => useAuthStore((state) => state.user?.role);
export const useHasHydrated = () => useAuthStore((state) => state._hasHydrated);

export const useHasPermission = (permission: string) =>
  useAuthStore((state) => state.user?.permissions.includes(permission) ?? false);

// Checks the user's full role set (see User.roles), falling back to the primary role for
// sessions persisted before `roles` existed.
export const useHasRole = (roles: UserRole[]) =>
  useAuthStore((state) => {
    const user = state.user;
    if (!user) return false;
    const granted = user.roles?.length ? user.roles : [user.role];
    return granted.some((role) => roles.includes(role));
  });

// Actions hook (stable reference - doesn't cause re-renders)
export const useAuthActions = () =>
  useAuthStore((state) => ({
    setUser: state.setUser,
    login: state.login,
    logout: state.logout,
  }));
